/* globals jQuery */
/**
 * Tools for svg.
 * @module svg
 * @license MIT
 * @copyright 2011 Jeff Schiller
 */

import { jsPDF } from 'jspdf/dist/jspdf.es.min.js';
import 'svg2pdf.js/dist/svg2pdf.es.js';
import jQueryPluginSVG from './jQuery.attr';
import {
    text2xml, cleanupElement, findDefs, getHref,
    toXml, getStrokedBBoxDefaultVisible, encode64, createObjectURL,
    dataURLToObjectURL, walkTree, getBBox as utilsGetBBox
} from './utilities';
import { transformPoint, transformListToTransform } from './math';
import { resetListMap } from './svgtransformlist';
import { convertUnit, shortFloat, convertToNum } from '../common/units';
import { isGecko, isChrome, isWebkit } from '../common/browser';
import * as pathModule from './path';
import { NS } from '../common/namespaces';
import * as draw from './draw';
import { recalculateDimensions } from './recalculate';
import { getParents, getClosest } from '../editor/jgraduate/Util.js';
import SvgCanvas from './svgcanvas';
import { BatchCommand, RemoveElementCommand, InsertElementCommand, ChangeElementCommand } from './undocommands';
import { zenDataServiceInstance, metadataServiceInstance } from '@/services';
import { runUntracked } from './mutation-tracking';


const $ = jQueryPluginSVG(jQuery);

interface ISVGInitContext {
    getNsMap: () => any //() { return nsMap; },
    getVisElems: () => any // () { return visElems; },
    getImportIds: (key) => any // (key) => this.importIds[key],
    setImportIds: (key, value) => any // (key, value) => this.importIds[key] = value,
    getrefAttrs: () => any // () { return refAttrs; },
    getcanvg: () => any // () { return canvg; },
}

let svgCanvas: SvgCanvas;
let svgContext_: ISVGInitContext = null;
let $id: typeof SvgCanvas["$id"] = null;

/**
* @function module:svg-exec.init
* @param {module:svg-exec.SvgCanvas#init} svgContext
* @returns {void}
*/
export const init = function (_svgCanvas: SvgCanvas, _svgContext: ISVGInitContext) {
    svgContext_ = _svgContext;
    svgCanvas = _svgCanvas;
    $id = svgCanvas.$id;
};

/**
* Main function to set up the SVG content for output.
* @function module:svgcanvas.SvgCanvas#svgCanvasToString
* @returns {string} The SVG image for output
*/
export const svgCanvasToString = function (this: SvgCanvas): string {
    // keep calling it until there are none to remove
    // (A real document change, so it is reported to Blazor like any other.)
    while (svgCanvas.removeUnusedDefElems() > 0) {
        // Intentionally ignored
    }

    svgCanvas.pathActions.clear(true);

    // Move out of in-group editing mode
    if (svgCanvas.getCurrentGroup()) {
        draw.leaveContext();
        svgCanvas.selectOnly([svgCanvas.getCurrentGroup()]);
    }

    // Serializing temporarily re-attaches metadata and unwraps nested SVGs, which are not document changes.
    return runUntracked(() => serializeCanvas.call(this));
};

const serializeCanvas = function (this: SvgCanvas): string {
    const nakedSvgs = [];

    const svgContent = svgCanvas.getSVGContent();
    metadataServiceInstance.reAddMetadata(svgContent);

    // Unwrap gsvg if it has no special attributes (only id and style)
    const gsvgElems = svgContent.querySelectorAll('g[data-gsvg]');
    Array.prototype.forEach.call(gsvgElems, function (element) {
        const attrs = element.attributes;
        let len = attrs.length;
        for (let i = 0; i < len; i++) {
            if (attrs[i].nodeName === 'id' || attrs[i].nodeName === 'style') {
                len--;
            }
        }
        // No significant attributes, so ungroup
        if (len <= 0) {
            const svg = element.firstChild;
            nakedSvgs.push(svg);
            element.replaceWith(svg);
        }
    });
    const output = this.svgToString(svgContent, 0);

    // Rewrap gsvg
    if (nakedSvgs.length) {
        Array.prototype.forEach.call(nakedSvgs, function (el) {
            svgCanvas.groupSvgElem(el);
        });
    }

    metadataServiceInstance.detachMetadata(svgContent);
    return output;
};

/**
* Sub function ran on each SVG element to convert it to a string as desired.
* @function module:svgcanvas.SvgCanvas#svgToString
* @param {Element} elem - The SVG element to convert
* @param {Integer} indent - Number of spaces to indent this tag
* @returns {string} The given element as an SVG tag
*/
export const svgToString = function (this: SvgCanvas, elem: SVGElement, indent: Integer): string {
    const curConfig = svgCanvas.getCurrentConfig();
    const nsMap = svgContext_.getNsMap();
    const out = [];
    const unit = curConfig.baseUnit;

    if (elem) {
        cleanupElement(elem);
        const childs = elem.childNodes;

        for (let i = 0; i < indent; i++) { out.push(' '); }
        out.push('<'); out.push(elem.nodeName);
        if (elem.id === 'svgcontent') {
            // Process root element separately
            const res = svgCanvas.getResolution();

            const vb = '';
            // TODO: Allow this by dividing all values by current baseVal
            // Note that this also means we should properly deal with this on import
            // if (curConfig.baseUnit !== 'px') {
            //   const unit = curConfig.baseUnit;
            //   const unitM = getTypeMap()[unit];
            //   res.w = shortFloat(res.w / unitM);
            //   res.h = shortFloat(res.h / unitM);
            //   vb = ' viewBox="' + [0, 0, res.w, res.h].join(' ') + '"';
            //   res.w += unit;
            //   res.h += unit;
            // }

            if (unit !== 'px') {
                res.w = convertUnit(res.w, unit) + unit;
                res.h = convertUnit(res.h, unit) + unit;
            }

            out.push(' width="' + res.w + '" height="' + res.h + '"' + vb + ' xmlns="' + NS.SVG + '" xmlns:zaa="' + NS.ZAA + '"');

            const nsuris = {
                [NS.ZAA]: true,
            };

            // Check elements for namespaces, add if found
            const csElements = elem.querySelectorAll('*');
            const cElements = Array.prototype.slice.call(csElements) as SVGElement[];
            cElements.push(elem);
            Array.prototype.forEach.call(cElements, function (el: SVGElement) {
                // const el = this;
                // for some elements have no attribute
                const uri = el.namespaceURI;
                if (uri && !nsuris[uri] && nsMap[uri] && nsMap[uri] !== 'xmlns' && nsMap[uri] !== 'xml' && !nsMap[uri].endsWith("_alias")) {
                    nsuris[uri] = true;
                    out.push(' xmlns:' + nsMap[uri].replace(/_/g, "") + '="' + uri + '"');
                }
                if (el.attributes.length > 0) {
                    for (const [, attr] of Object.entries<Attr>(el.attributes)) {
                        const u = attr.namespaceURI;
                        if (u && !nsuris[u] && nsMap[u] !== 'xmlns' && nsMap[u] !== 'xml' && !nsMap[u].endsWith("_alias")) {
                            nsuris[u] = true;
                            out.push(' xmlns:' + nsMap[u].replace(/_/g, "") + '="' + u + '"');
                        }
                    }
                }
            });

            for (const [name, value] of getRootExportAttributes(elem as SVGSVGElement)) {
                out.push(' ');
                out.push(name); out.push('="');
                out.push(value); out.push('"');
            }
        } else {
            // Skip empty defs
            if (elem.nodeName === 'defs' && !elem.firstChild) {
                return '';
            }

            for (const [name, value] of getExportAttributes(elem)) {
                out.push(' ');
                out.push(name); out.push('="');
                out.push(value); out.push('"');
            }
        }

        if (elem.hasChildNodes()) {
            out.push('>');
            indent++;
            let bOneLine = false;

            for (let i = 0; i < childs.length; i++) {
                const child = childs.item(i);
                switch (child.nodeType) {
                    case Node.ELEMENT_NODE: // element node
                        out.push('\n');
                        out.push(this.svgToString(child as SVGElement, indent));
                        break;
                    case Node.TEXT_NODE: { // text node
                        const str = child.nodeValue.replace(/(?:^\s+|\s+$)/g, '');
                        if (str !== '') {
                            bOneLine = true;
                            out.push(String(toXml(str)));
                        }
                        break;
                    } case Node.CDATA_SECTION_NODE: // cdata node
                        out.push('\n');
                        out.push(new Array(indent + 1).join(' '));
                        out.push('<![CDATA[');
                        out.push(child.nodeValue);
                        out.push(']]>');
                        break;
                    case Node.COMMENT_NODE: // comment
                        const commentNode = child as Comment;
                        out.push('\n');
                        out.push(new Array(indent + 1).join(' '));
                        out.push('<!--');
                        out.push(commentNode.data);
                        out.push('-->');
                        break;
                } // switch on node type
            }
            indent--;
            if (!bOneLine) {
                out.push('\n');
                for (let i = 0; i < indent; i++) { out.push(' '); }
            }
            out.push('</'); out.push(elem.nodeName); out.push('>');
        } else {
            out.push('/>');
        }
    }
    return out.join('');
}; // end svgToString()

/**
 * Gets the attributes of the root (#svgcontent) element as `svgToString` writes them, excluding the size and namespace
 * declarations it writes separately.
 * @param elem The #svgcontent element.
 * @returns The attribute names and values, in output order.
 */
const getRootExportAttributes = function (elem: SVGSVGElement): [string, string][] {
    const nsMap = svgContext_.getNsMap();
    const attrs = [...elem.attributes].sort((a, b) => a.name > b.name ? -1 : 1);
    const attrNames = ['width', 'height', 'xmlns', 'x', 'y', 'viewBox', 'id', 'overflow'];
    const out: [string, string][] = [];

    let i = attrs.length;
    while (i--) {
        const attr = attrs[i];
        const attrVal = toXml(attr.value);

        // Namespaces are written separately, so skip
        if (attr.nodeName.startsWith('xmlns:')) { continue; }

        // only serialize attributes we don't use internally
        if (attrVal !== '' && !attrNames.includes(attr.localName) && (!attr.namespaceURI || nsMap[attr.namespaceURI])) {
            out.push([attr.nodeName, attrVal]);
        }
    }

    return out;
};

/**
 * Gets every attribute of the root (#svgcontent) element as it appears in the document, including its size but not
 * its namespace declarations.
 * @param elem The #svgcontent element.
 * @returns The attribute names and values.
 */
export const getDocumentRootAttributes = function (elem: SVGSVGElement): [string, string][] {
    const unit = svgCanvas.getCurrentConfig().baseUnit;
    const res = svgCanvas.getResolution();
    const width = unit !== 'px' ? convertUnit(res.w, unit) + unit : String(res.w);
    const height = unit !== 'px' ? convertUnit(res.h, unit) + unit : String(res.h);

    return [['width', width], ['height', height], ...getRootExportAttributes(elem)];
};

/**
 * Gets the attributes of a (non-root) element as they appear in the document: as `svgToString` writes them, in output
 * order, with editor-only attributes removed and values normalised.
 * @param elem The element. Call `cleanupElement` on it first, as `svgToString` does.
 * @returns The attribute names and values.
 */
export const getExportAttributes = function (elem: Element): [string, string][] {
    const nsMap = svgContext_.getNsMap();
    const unit = svgCanvas.getCurrentConfig().baseUnit;
    const unitRe = new RegExp('^-?[\\d\\.]+' + unit + '$');
    const attrs = [...elem.attributes].sort((a, b) => a.name > b.name ? -1 : 1);
    const mozAttrs = ['-moz-math-font-style', '_moz-math-font-style'];

    // While editing inside a group, every other element is dimmed. That is editor display state, so report the
    // opacity the element really has.
    const contextOpacity = draw.getContextOriginalOpacity(elem);

    const out: [string, string][] = [];
    for (let i = attrs.length - 1; i >= 0; i--) {
        const attr = attrs[i];
        let attrVal = toXml(attr.value);
        // remove bogus attributes added by Gecko
        if (mozAttrs.includes(attr.localName)) { continue; }
        if (contextOpacity !== undefined && attr.localName === 'opacity') {
            // cleanupElement drops opacity="1" as the default, so an original of "1" is written as no attribute too.
            if (contextOpacity === null || contextOpacity === '1') { continue; }
            attrVal = toXml(contextOpacity);
        }
        if (attrVal === 'null') {
            const styleName = attr.localName.replace(/-[a-z]/g, (s) => s[1].toUpperCase());
            if (Object.prototype.hasOwnProperty.call((elem as SVGElement).style, styleName)) { continue; }
        }
        if (attrVal !== '') {
            if (attrVal.startsWith('pointer-events')) { continue; }
            if (attr.localName === 'class' && attrVal.startsWith('se_')) { continue; }

            if (attr.localName === 'd') {
                attrVal = svgCanvas.pathActions.convertPath(elem as SVGPathElement, true);
            }

            const attrValNum = parseFloat(attrVal);
            if (!isNaN(attrValNum) && attrValNum.toString() == attrVal) {
                // shortFloat returns a number. Keep values strings, as they are sent to Blazor as well as written out.
                attrVal = String(shortFloat(attrVal));
            } else if (unitRe.test(attrVal)) {
                attrVal = shortFloat(attrVal) + unit;
            }

            if (elem.nodeName === 'image') {
                if (attr.localName === 'href') {
                    if (elem.hasAttribute('href-orig')) {
                        attrVal = elem.getAttribute('href-orig');
                    }

                    out.push([`xlink:${attr.nodeName}`, attrVal]);
                } else if (attr.localName === 'href-orig') {
                    continue;
                }

                // Embed images when saving
                if (svgCanvas.getSaveOptions().apply &&
                    attr.localName === 'href' &&
                    svgCanvas.getSaveOptions().images === 'embed'
                ) {
                    const img = svgCanvas["encodableImages"][attrVal];
                    if (img) { attrVal = img; }
                }
            }

            // map various namespaces to our fixed namespace prefixes
            // (the default xmlns attribute itself does not get a prefix)
            if (!attr.namespaceURI || attr.namespaceURI === NS.SVG || nsMap[attr.namespaceURI]) {
                out.push([attr.nodeName, attrVal]);
            }
        }
    }

    return out;
};

/**
* This function sets the current drawing as the input SVG XML.
* @function module:svgcanvas.SvgCanvas#setSvgString
* @param {string} xmlString - The SVG as XML text.
* @param {boolean} [preventUndo=false] - Indicates if we want to do the
* changes without adding them to the undo stack - e.g. for initializing a
* drawing on page load.
* @fires module:svgcanvas.SvgCanvas#event:setnonce
* @fires module:svgcanvas.SvgCanvas#event:unsetnonce
* @fires module:svgcanvas.SvgCanvas#event:changed
* @returns {boolean} This function returns `false` if the set was
*     unsuccessful, `true` otherwise.
*/
export const setSvgString = function (this: SvgCanvas, xmlString: string, preventUndo: boolean = false): boolean {
    // Replaces the whole document, which Blazor takes as a new baseline (see svgcontent-changed), rather than as
    // individual changes.
    return runUntracked(() => setSvgStringInternal.call(this, xmlString, preventUndo));
};

const setSvgStringInternal = function (this: SvgCanvas, xmlString: string, preventUndo: boolean = false): boolean {
    const curConfig = svgCanvas.getCurrentConfig();
    const dataStorage = svgCanvas.getDataStorage();

    // Inject xmlns:zaa into the root <svg element if it's not already there
    if (!/<svg[^>]+xmlns:zaa=/.test(xmlString)) {
        xmlString = xmlString.replace(/<svg/, '<svg xmlns:zaa="' + NS.ZAA + '"');
    }

    try {
        // convert string into XML document
        const newDoc = text2xml(xmlString);
        if (newDoc.firstElementChild && newDoc.firstElementChild.namespaceURI !== NS.SVG) {
            const err = newDoc.querySelector("parsererror > div")?.innerHTML;
            if (err) {
                console.error(err, newDoc);
            }

            return false;
        }

        metadataServiceInstance.extractMetadata(newDoc.documentElement);
        this.prepareSvg(newDoc);

        const batchCmd = new BatchCommand('Change Source');

        // remove old svg document
        const oldSvgContent = svgCanvas.getSVGContent();

        oldSvgContent.remove();
        batchCmd.addSubCommand(new RemoveElementCommand(oldSvgContent, oldSvgContent.nextSibling, svgCanvas.getSVGRoot()));

        // set new svg document
        // If DOM3 adoptNode() available, use it. Otherwise fall back to DOM2 importNode()
        const newDocEl = newDoc.documentElement as unknown as SVGSVGElement;
        svgCanvas["svgcontent"] = svgCanvas.getDOMDocument().adoptNode
            ? svgCanvas.getDOMDocument().adoptNode(newDocEl)
            : svgCanvas.getDOMDocument().importNode(newDocEl, true)

        svgCanvas.getSVGRoot().append(svgCanvas.getSVGContent());
        const content = svgCanvas.getSVGContent();

        svgCanvas["current_drawing_"] = new draw.Drawing(svgCanvas.getSVGContent(), svgCanvas.getIdPrefix());

        // retrieve or set the nonce
        const nonce = svgCanvas.getCurrentDrawing().getNonce();
        if (nonce) {
            svgCanvas.call('setnonce', nonce);
        } else {
            svgCanvas.call('unsetnonce');
        }

        // change image href vals if possible
        const elements = content.querySelectorAll('image');
        const directoryUrl = this.editor.getCurrentFileDirectoryUrl();

        Array.prototype.forEach.call(elements, (image: SVGImageElement) => {
            const href = svgCanvas.getHref(image);
            if (href && !href.startsWith("/api/file?url=")) {
                const newHref = `/api/file?url=${encodeURIComponent(directoryUrl + href)}`;
                image.setAttribute("href-orig", href);
                image.setAttribute("href", newHref);
                image.removeAttribute("xlink:href");

                setTimeout(() => {
                    image.href.baseVal = `${newHref}?x=${Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)}`;
                });
            }
        });

        // Wrap child SVGs in group elements
        const svgElements = content.querySelectorAll('svg');
        Array.prototype.forEach.call(svgElements, function (element) {
            // Skip if it's in a <defs>
            if (getClosest(element.parentNode, 'defs')) {
                return;
            }

            svgCanvas.uniquifyElems(element);

            // Check if it already has a gsvg group
            const pa = element.parentNode;
            if (pa.childNodes.length === 1 && pa.nodeName === 'g') {
                dataStorage.put(pa, 'gsvg', element);
                pa.id = pa.id || svgCanvas.getNextId();
            } else {
                svgCanvas.groupSvgElem(element);
            }
        });

        // For Firefox: Put all paint elems in defs
        if (isGecko()) {
            const svgDefs = findDefs();
            const findElems = content.querySelectorAll('linearGradient, radialGradient, pattern');
            Array.prototype.forEach.call(findElems, function (ele) {
                svgDefs.appendChild(ele);
            });
        }

        // Set ref element for <use> elements

        // TODO: This should also be done if the object is re-added through "redo"
        svgCanvas.setUseData(content);
        svgCanvas.convertGradients(content);

        const attrs: PlainObject = {
            id: 'svgcontent',
            overflow: curConfig.show_outside_canvas ? 'visible' : 'hidden'
        };

        let percs = false;

        // determine proper size
        if (content.getAttribute('viewBox')) {
            const viBox = content.getAttribute('viewBox');
            const vb = viBox.split(' ');
            attrs.width = vb[2];
            attrs.height = vb[3];
            // handle content that doesn't have a viewBox
        } else {
            ['width', 'height'].forEach(function (dim) {
                // Set to 100 if not given
                const val = content.getAttribute(dim) || '100%';
                if (String(val).endsWith('%')) {
                    // Use user units if percentage given
                    percs = true;
                } else {
                    attrs[dim] = convertToNum(dim, val);
                }
            });
        }

        // identify layers
        draw.identifyLayers();

        // Give ID for any visible layer children missing one
        // Note: In artwork mode, the metadata service will handle this instead.
        if(zenDataServiceInstance.GetDesignerMode() !== "artwork") {
            const chiElems = content.children;
            Array.prototype.forEach.call(chiElems, function (chiElem) {
                const visElems = chiElem.querySelectorAll(svgContext_.getVisElems());
                Array.prototype.forEach.call(visElems, function (elem) {
                    if (!elem.id) {
                        elem.id = svgCanvas.getNextId();
                    }
                });
            });
        }

        // Percentage width/height, so let's base it on visible elements
        if (percs) {
            const bb = getStrokedBBoxDefaultVisible();
            attrs.width = bb.width + bb.x;
            attrs.height = bb.height + bb.y;
        }

        // Just in case negative numbers are given or
        // result from the percs calculation
        if (attrs.width <= 0) {
            attrs.width = 100;
        }
        if (attrs.height <= 0) {
            attrs.height = 100;
        }

        for (const [key, value] of Object.entries(attrs)) {
            content.setAttribute(key, value);
        }
        this.contentW = attrs.width;
        this.contentH = attrs.height;

        batchCmd.addSubCommand(new InsertElementCommand(svgCanvas.getSVGContent()));
        // update root to the correct size
        const width = content.getAttribute('width');
        const height = content.getAttribute('height');
        const changes = { width: width, height: height };
        batchCmd.addSubCommand(new ChangeElementCommand(svgCanvas.getSVGRoot(), changes));

        // reset zoom
        svgCanvas.setCurrentZoom(1);

        // reset transform lists
        resetListMap();
        svgCanvas.clearSelection();
        pathModule.clearData();
        svgCanvas.getSVGRoot().append(svgCanvas.selectorManager.selectorParentGroup);

        if (!preventUndo) {
            svgCanvas.addCommandToHistory(batchCmd);
        }

        svgCanvas.editor.refreshUI();

        const newSvgContent = svgCanvas.getSVGContent();
        window.dispatchEvent(new CustomEvent("svgcontent-changed", { detail: newSvgContent }));
        svgCanvas.call('changed', [newSvgContent]);
    } catch (e) {
        console.log(e);
        return false;
    }

    return true;
};

/**
* This function imports the input SVG XML as a `<symbol>` in the `<defs>`, then adds a
* `<use>` to the current layer.
* @function module:svgcanvas.SvgCanvas#importSvgString
* @param {string} xmlString - The SVG as XML text.
* @fires module:svgcanvas.SvgCanvas#event:changed
* @returns {null|SVGElement} This function returns null if the import was unsuccessful, or the element otherwise.
* @todo
* - properly handle if namespace is introduced by imported content (must add to svgcontent
* and update all prefixes in the imported node)
* - properly handle recalculating dimensions, `recalculateDimensions()` doesn't handle
* arbitrary transform lists, but makes some assumptions about how the transform list
* was obtained
*/
export const importSvgString = function (this: SvgCanvas, xmlString: string): SVGElement | null {
    const dataStorage = svgCanvas.getDataStorage();
    let j, ts, useEl;
    try {
        // Get unique ID
        const uid = encode64(xmlString.length + xmlString).substring(0, 32);

        let useExisting = false;
        // Look for symbol and make sure symbol exists in image
        const existing = svgContext_.getImportIds(uid);
        if (existing?.symbol) {
            const parents = getParents(existing.symbol, '#svgroot');
            if (parents.length) {
                useExisting = true;
            }
        }

        const batchCmd = new BatchCommand('Import Image');
        let symbol;
        if (useExisting) {
            ({ symbol } = svgContext_.getImportIds(uid));
            ts = svgContext_.getImportIds(uid).xform;
        } else {
            // convert string into XML document
            const newDoc = text2xml(xmlString);

            this.prepareSvg(newDoc);

            // import new svg document into our document
            // If DOM3 adoptNode() available, use it. Otherwise fall back to DOM2 importNode()
            const newDocEl = newDoc.documentElement as unknown as SVGSVGElement;
            const svg = svgCanvas.getDOMDocument().adoptNode
                ? svgCanvas.getDOMDocument().adoptNode(newDocEl)
                : svgCanvas.getDOMDocument().importNode(newDocEl, true);

            svgCanvas.uniquifyElems(svg);

            const innerw = convertToNum('width', svg.getAttribute('width'));
            const innerh = convertToNum('height', svg.getAttribute('height'));
            const innervb = svg.getAttribute('viewBox');
                // if no explicit viewbox, create one out of the width and height
            const vb = innervb
                ? innervb.split(' ').map(s => Number(s))
                : [0, 0, innerw, innerh];

            // TODO: properly handle preserveAspectRatio
            const // canvasw = +svgcontent.getAttribute('width'),
                canvash = Number(svgCanvas.getSVGContent().getAttribute('height'));
            // imported content should be 1/3 of the canvas on its largest dimension

            ts = innerh > innerw ? 'scale(' + (canvash / 3) / vb[3] + ')' : 'scale(' + (canvash / 3) / vb[2] + ')';

            // Hack to make recalculateDimensions understand how to scale
            ts = 'translate(0) ' + ts + ' translate(0)';

            symbol = svgCanvas.getDOMDocument().createElementNS(NS.SVG, 'symbol');
            const defs = findDefs();

            if (isGecko()) {
                // Move all gradients into root for Firefox, workaround for this bug:
                // https://bugzilla.mozilla.org/show_bug.cgi?id=353575
                // TODO: Make this properly undo-able.
                const elements = svg.querySelectorAll('linearGradient, radialGradient, pattern');
                Array.prototype.forEach.call(elements, function (el) {
                    defs.appendChild(el);
                });
            }

            while (svg.firstChild) {
                const first = svg.firstChild;
                symbol.append(first);
            }
            const attrs = svg.attributes;
            for (const attr of attrs) { // Ok for `NamedNodeMap`
                symbol.setAttribute(attr.nodeName, attr.value);
            }
            symbol.id = svgCanvas.getNextId();

            // Store data
            svgContext_.setImportIds(uid, {
                symbol,
                xform: ts
            });

            findDefs().append(symbol);
            batchCmd.addSubCommand(new InsertElementCommand(symbol));
        }

        useEl = svgCanvas.getDOMDocument().createElementNS(NS.SVG, 'use');
        useEl.id = svgCanvas.getNextId();
        svgCanvas.setHref(useEl, '#' + symbol.id);

        (svgCanvas.getCurrentGroup() || svgCanvas.getCurrentDrawing().getCurrentLayer()).append(useEl);
        batchCmd.addSubCommand(new InsertElementCommand(useEl));
        svgCanvas.clearSelection();

        useEl.setAttribute('transform', ts);
        recalculateDimensions(useEl);
        dataStorage.put(useEl, 'symbol', symbol);
        dataStorage.put(useEl, 'ref', symbol);
        svgCanvas.addToSelection([useEl]);

        // TODO: Find way to add this in a recalculateDimensions-parsable way
        // if (vb[0] !== 0 || vb[1] !== 0) {
        //   ts = 'translate(' + (-vb[0]) + ',' + (-vb[1]) + ') ' + ts;
        // }
        svgCanvas.addCommandToHistory(batchCmd);
        svgCanvas.call('changed', [svgCanvas.getSVGContent()]);
    } catch (e) {
        console.log(e);
        return null;
    }

    // we want to return the element so we can automatically select it
    return useEl;
};
/**
 * Function to run when image data is found.
 * @callback module:svgcanvas.ImageEmbeddedCallback
 * @param {string|false} result Data URL
 * @returns {void}
 */
export type ImageEmbeddedCallback = (result: string) => void;

/**
* Converts a given image file to a data URL when possible, then runs a given callback.
* @function module:svgcanvas.SvgCanvas#embedImage
* @param {string} src - The path/URL of the image
* @returns {Promise<string|false>} Resolves to a Data URL (string|false)
*/
export const embedImage = function (src: string): Promise<string | false> {
    // Todo: Remove this Promise in favor of making an async/await `Image.load` utility
    return new Promise(function (resolve, reject) {
        // load in the image and once it's loaded, get the dimensions
        const imgI = new Image();
        imgI.addEventListener("load", (e) => {
            // create a canvas the same size as the raster image
            const target = e.currentTarget as HTMLImageElement;
            const cvs = document.createElement('canvas');
            cvs.width = target.width;
            cvs.height = target.height;
            // load the raster image into the canvas
            cvs.getContext('2d').drawImage(target, 0, 0);
            // retrieve the data: URL
            try {
                let urldata = ';svgedit_url=' + encodeURIComponent(src);
                urldata = cvs.toDataURL().replace(';base64', urldata + ';base64');
                svgCanvas["encodableImages"][src] = urldata;
            } catch (e) {
                svgCanvas["encodableImages"][src] = false;
            }
            svgCanvas.setGoodImage(src);
            resolve(svgCanvas["encodableImages"][src]);
        });
        imgI.addEventListener("error", () => {
            reject(new Error('Error loading image: '));
        });
        imgI.setAttribute('src', src);
    });
};

/**
* @typedef {PlainObject} module:svgcanvas.SaveOptions
* @property {boolean} apply
* @property {"embed"} [image]
* @property {Integer} round_digits
*/
export interface SaveOptions {
    apply?: boolean;
    image?: "embed";
    round_digits: Integer;
    images?: any; // TODO: Fix as any
}

/**
* Serializes the current drawing into SVG XML text and passes it to the 'saved' handler.
* This function also includes the XML prolog. Clients of the `SvgCanvas` bind their save
* function to the 'saved' event.
* @function module:svgcanvas.SvgCanvas#save
* @param {module:svgcanvas.SaveOptions} opts
* @fires module:svgcanvas.SvgCanvas#event:saved
* @returns {void}
*/
export const save = function (opts: SaveOptions) {
    // remove the selected outline before serializing
    svgCanvas.clearSelection();
    // Update save options if provided
    if (opts) {
        $.extend(svgCanvas.getSaveOptions(), opts);
    }
    svgCanvas.setSaveOption('apply', true);

    // no need for doctype, see https://jwatt.org/svg/authoring/#doctype-declaration
    const str = svgCanvas.svgCanvasToString();
    svgCanvas.call('saved', str);
};

/**
  * @typedef {"feGaussianBlur"|"foreignObject"|"[stroke-dasharray]"|"text"} module:svgcanvas.IssueCode
*/
export type IssueCode = "feGaussianBlur" | "foreignObject" | "[stroke-dasharray]" | "text";

/**
* @typedef {PlainObject} module:svgcanvas.IssuesAndCodes
* @property {IssueCode[]} issueCodes The locale-independent code names
* @property {string[]} issues The localized descriptions
*/
export interface IssuesAndCodes {
    /** The locale-independent code names */
    issueCodes: IssueCode[];
    /** The localized descriptions */
    issues: string[];
}

/**
* Codes only is useful for locale-independent detection.
* @returns {module:svgcanvas.IssuesAndCodes}
*/
function getIssues(): IssuesAndCodes {
    const uiStrings = svgCanvas.getUIStrings();
    // remove the selected outline before serializing
    svgCanvas.clearSelection();

    // Check for known CanVG issues
    const issues = [];
    const issueCodes = [];

    // Selector and notice
    const issueList: PlainObject = {
        feGaussianBlur: uiStrings.exportNoBlur,
        foreignObject: uiStrings.exportNoforeignObject,
        '[stroke-dasharray]': uiStrings.exportNoDashArray
    };
    const content = svgCanvas.getSVGContent();

    // Add font/text check if Canvas Text API is not implemented
    if (!('font' in document.querySelector<HTMLCanvasElement>('CANVAS').getContext('2d'))) {
        issueList.text = uiStrings.exportNoText;
    }

    for (const [sel, descr] of Object.entries(issueList)) {
        if (content.querySelectorAll(sel).length) {
            issueCodes.push(sel);
            issues.push(descr);
        }
    }
    return { issues, issueCodes };
}

export type ImageType = "PNG" | "JPEG" | "BMP" | "WEBP" | "ICO";
export type ImageMimeType = "image/png" | "image/jpeg" | "image/bmp" | "image/webp";

/**
* @typedef {PlainObject} module:svgcanvas.ImageExportedResults
* @property {string} datauri Contents as a Data URL
* @property {string} bloburl May be the empty string
* @property {string} svg The SVG contents as a string
* @property {string[]} issues The localization messages of `issueCodes`
* @property {module:svgcanvas.IssueCode[]} issueCodes CanVG issues found with the SVG
* @property {"PNG"|"JPEG"|"BMP"|"WEBP"|"ICO"} type The chosen image type
* @property {"image/png"|"image/jpeg"|"image/bmp"|"image/webp"} mimeType The image MIME type
* @property {Float} quality A decimal between 0 and 1 (for use with JPEG or WEBP)
* @property {string} exportWindowName A convenience for passing along a `window.name` to target a window on which the export could be added
*/
export interface ImageExportedResults {
    datauri: string;
    bloburl: string;
    svg: string;
    issues: string[];
    issueCodes: IssueCode[];
    type: ImageType;
    mimeType: ImageMimeType;
    quality: Float;
    exportWindowName: string;
}

/**
* Generates a PNG (or JPG, BMP, WEBP) Data URL based on the current image,
* then calls "exported" with an object including the string, image
* information, and any issues found.
* @function module:svgcanvas.SvgCanvas#rasterExport
* @param {"PNG"|"JPEG"|"BMP"|"WEBP"|"ICO"} [imgType="PNG"]
* @param {Float} [quality] Between 0 and 1
* @param {string} [exportWindowName]
* @param {PlainObject} [opts]
* @param {boolean} [opts.avoidEvent]
* @fires module:svgcanvas.SvgCanvas#event:exported
* @todo Confirm/fix ICO type
* @returns {Promise<module:svgcanvas.ImageExportedResults>} Resolves to {@link module:svgcanvas.ImageExportedResults}
*/
export const rasterExport = async function (this: SvgCanvas, imgType: ImageType = "PNG", quality?: Float, exportWindowName?: string, opts: { [key: string]: any, avoidEvent?: boolean } = {}): Promise<ImageExportedResults> {
    const type = imgType === 'ICO' ? 'BMP' : (imgType || 'PNG');
    const mimeType: ImageMimeType = 'image/' + type.toLowerCase() as ImageMimeType;
    const { issues, issueCodes } = getIssues();
    const svg = this.svgCanvasToString();

    if (!$id('export_canvas')) {
        const canvasEx = document.createElement('CANVAS');
        canvasEx.id = 'export_canvas';
        canvasEx.style.display = 'none';
        document.body.appendChild(canvasEx);
    }
    const c = $id<HTMLCanvasElement>('export_canvas');
    c.style.width = svgCanvas.contentW + "px";
    c.style.height = svgCanvas.contentH + "px";
    const canvg = svgContext_.getcanvg();
    const ctx = c.getContext('2d');
    const v = canvg.fromString(ctx, svg);
    // Render only first frame, ignoring animations.
    await v.render();
    // Todo: Make async/await utility in place of `toBlob`, so we can remove this constructor
    return new Promise((resolve) => {
        const dataURLType = type.toLowerCase();
        const datauri = quality
            ? c.toDataURL('image/' + dataURLType, quality)
            : c.toDataURL('image/' + dataURLType);
        let bloburl;
        /**
     * Called when `bloburl` is available for export.
     * @returns {void}
     */
        function done() {
            const obj = {
                datauri,
                bloburl,
                svg,
                issues,
                issueCodes,
                type: imgType,
                mimeType,
                quality,
                exportWindowName
            };
            if (!opts.avoidEvent) {
                svgCanvas.call('exported', obj);
            }
            resolve(obj);
        }
        if (c.toBlob) {
            c.toBlob((blob) => {
                bloburl = createObjectURL(blob);
                done();
            }, mimeType, quality);
            return;
        }
        bloburl = dataURLToObjectURL(datauri);
        done();
    });
};

/**
* @typedef {void|"save"|"arraybuffer"|"blob"|"datauristring"|"dataurlstring"|"dataurlnewwindow"|"datauri"|"dataurl"} external:jsPDF.OutputType
* @todo Newer version to add also allows these `outputType` values "bloburi"|"bloburl" which return strings, so document here and for `outputType` of `module:svgcanvas.PDFExportedResults` below if added
*/
export type jsPDFOutputType = void | "save" | "arraybuffer" | "blob" | "datauristring" | "dataurlstring" | "dataurlnewwindow" | "datauri" | "dataurl";

/**
* @typedef {PlainObject} module:svgcanvas.PDFExportedResults
* @property {string} svg The SVG PDF output
* @property {string|ArrayBuffer|Blob|window} output The output based on the `outputType`;
* if `undefined`, "datauristring", "dataurlstring", "datauri",
* or "dataurl", will be a string (`undefined` gives a document, while the others
* build as Data URLs; "datauri" and "dataurl" change the location of the current page); if
* "arraybuffer", will return `ArrayBuffer`; if "blob", returns a `Blob`;
* if "dataurlnewwindow", will change the current page's location and return a string
* if in Safari and no window object is found; otherwise opens in, and returns, a new `window`
* object; if "save", will have the same return as "dataurlnewwindow" if
* `navigator.getUserMedia` support is found without `URL.createObjectURL` support; otherwise
* returns `undefined` but attempts to save
* @property {external:jsPDF.OutputType} outputType
* @property {string[]} issues The human-readable localization messages of corresponding `issueCodes`
* @property {module:svgcanvas.IssueCode[]} issueCodes
* @property {string} exportWindowName
*/

export interface PDFExportedResults {
    // svg: string;
    output: string | ArrayBuffer | Blob | Window;
    outputType: jsPDFOutputType;
    issues: string[];
    issueCodes: IssueCode[];
    exportWindowName: string;
}

/**
* Generates a PDF based on the current image, then calls "exportedPDF" with
* an object including the string, the data URL, and any issues found.
* @function module:svgcanvas.SvgCanvas#exportPDF
* @param {string} [exportWindowName] Will also be used for the download file name here
* @param {external:jsPDF.OutputType} [outputType="dataurlstring"]
* @fires module:svgcanvas.SvgCanvas#event:exportedPDF
* @returns {Promise<module:svgcanvas.PDFExportedResults>} Resolves to {@link module:svgcanvas.PDFExportedResults}
*/
export const exportPDF = async (exportWindowName?: string, outputType: jsPDFOutputType = isChrome() ? 'save' : undefined): Promise<PDFExportedResults> => {
    const res = svgCanvas.getResolution();
    const orientation = res.w > res.h ? 'landscape' : 'portrait';
    const unit = 'pt'; // curConfig.baseUnit; // We could use baseUnit, but that is presumably not intended for export purposes

    // Todo: Give options to use predefined jsPDF formats like "a4", etc. from pull-down (with option to keep customizable)
    const doc = jsPDF({
        orientation,
        unit,
        format: [res.w, res.h]
        // , compressPdf: true
    });
    const docTitle = svgCanvas.getDocumentTitle();
    doc.setProperties({
        title: docTitle /* ,
    subject: '',
    author: '',
    keywords: '',
    creator: '' */
    });
    const { issues, issueCodes } = getIssues();

    await doc.svg(svgCanvas.getSVGContent(), { x: 0, y: 0, width: res.w, height: res.h });

    // doc.output('save'); // Works to open in a new
    //  window; todo: configure this and other export
    //  options to optionally work in this manner as
    //  opposed to opening a new tab
    outputType = outputType || 'dataurlstring';
    const obj: PDFExportedResults = {
        issues,
        issueCodes,
        exportWindowName,
        outputType,
        output: doc.output(outputType, outputType === 'save' ? (exportWindowName || 'svg.pdf') : undefined),
    };

    svgCanvas.call('exportedPDF', obj);
    return obj;
};
/**
* Ensure each element has a unique ID.
* @function module:svgcanvas.SvgCanvas#uniquifyElems
* @param {Element} g - The parent element of the tree to give unique IDs
* @returns {void}
*/
export const uniquifyElemsMethod = function (g: SVGElement) {
    const ids = {};
    // TODO: Handle markers and connectors. These are not yet re-identified properly
    // as their referring elements do not get remapped.
    //
    // <marker id='se_marker_end_svg_7'/>
    // <polyline id='svg_7' se:connector='svg_1 svg_6' marker-end='url(#se_marker_end_svg_7)'/>
    //
    // Problem #1: if svg_1 gets renamed, we do not update the polyline's se:connector attribute
    // Problem #2: if the polyline svg_7 gets renamed, we do not update the marker id nor the polyline's marker-end attribute
    const refElems = ['filter', 'linearGradient', 'pattern', 'radialGradient', 'symbol', 'textPath', 'use'];

    walkTree(g, function (n) {
        // if it's an element node
        if (n.nodeType === Node.ELEMENT_NODE) {
            // and the element has an ID
            if (n.id) {
                // and we haven't tracked this ID yet
                if (!(n.id in ids)) {
                    // add this id to our map
                    ids[n.id] = { elem: null, attrs: [], hrefs: [] };
                }
                ids[n.id].elem = n;
            }

            // now search for all attributes on this element that might refer
            // to other elements
            svgContext_.getrefAttrs().forEach(function (attr) {
                const attrnode = n.getAttributeNode(attr);
                if (attrnode) {
                    // the incoming file has been sanitized, so we should be able to safely just strip off the leading #
                    const url = svgCanvas.getUrlFromAttr(attrnode.value),
                        refid = url ? url.substring(1) : null;
                    if (refid) {
                        if (!(refid in ids)) {
                            // add this id to our map
                            ids[refid] = { elem: null, attrs: [], hrefs: [] };
                        }
                        ids[refid].attrs.push(attrnode);
                    }
                }
            });

            // check xlink:href now
            const href = svgCanvas.getHref(n);
            // TODO: what if an <image> or <a> element refers to an element internally?
            if (href && refElems.includes(n.nodeName)) {
                const refid = href.substring(1);
                if (refid) {
                    if (!(refid in ids)) {
                        // add this id to our map
                        ids[refid] = { elem: null, attrs: [], hrefs: [] };
                    }
                    ids[refid].hrefs.push(n);
                }
            }
        }
    });

    // in ids, we now have a map of ids, elements and attributes, let's re-identify
    for (const oldid in ids) {
        if (!oldid) { continue; }
        const { elem } = ids[oldid];
        if (elem) {
            const newid = svgCanvas.getNextId();

            // assign element its new id
            elem.id = newid;

            // remap all url() attributes
            const { attrs } = ids[oldid];
            let j = attrs.length;
            while (j--) {
                const attr = attrs[j];
                attr.ownerElement.setAttribute(attr.name, 'url(#' + newid + ')');
            }

            // remap all href attributes
            const hreffers = ids[oldid].hrefs;
            let k = hreffers.length;
            while (k--) {
                const hreffer = hreffers[k];
                svgCanvas.setHref(hreffer, '#' + newid);
            }
        }
    }
};

/**
* Assigns reference data for each use element.
* @function module:svgcanvas.SvgCanvas#setUseData
* @param {Element} parent
* @returns {void}
*/
export const setUseDataMethod = function (parent: SVGElement) {
    let elems = [parent];

    if (parent.tagName !== 'use') {
        elems = [...parent.querySelectorAll('use')];
    }

    Array.prototype.forEach.call(elems, function (el, _) {
        const dataStorage = svgCanvas.getDataStorage();
        const id = svgCanvas.getHref(el).substring(1);
        const refElem = svgCanvas.getElem(id);
        if (!refElem) {
            return;
        }

        dataStorage.put(el, 'ref', refElem);
        if (refElem.tagName === 'symbol' || refElem.tagName === 'svg') {
            dataStorage.put(el, 'symbol', refElem);
            dataStorage.put(el, 'ref', refElem);
        }
    });
};

/**
* Looks at DOM elements inside the `<defs>` to see if they are referred to,
* removes them from the DOM if they are not.
* @function module:svgcanvas.SvgCanvas#removeUnusedDefElems
* @returns {Integer} The number of elements that were removed
*/
export const removeUnusedDefElemsMethod = function (): Integer {
    const defs = svgCanvas.getSVGContent().getElementsByTagNameNS(NS.SVG, 'defs');
    if (!defs?.length) {
        return 0;
    }

    const defelemUses = [];
    let numRemoved = 0;
    const attrs = ['fill', 'stroke', 'filter', 'marker-start', 'marker-mid', 'marker-end'];
    const alen = attrs.length;

    const allEls = svgCanvas.getSVGContent().getElementsByTagNameNS(NS.SVG, '*');
    const allLen = allEls.length;

    let i, j;
    for (i = 0; i < allLen; i++) {
        const el = allEls[i];
        for (j = 0; j < alen; j++) {
            const ref = svgCanvas.getUrlFromAttr(el.getAttribute(attrs[j]));
            if (ref) {
                defelemUses.push(ref.substring(1));
            }
        }

        // gradients can refer to other gradients
        const href = getHref(el);
        if (href && href.startsWith('#')) {
            defelemUses.push(href.substring(1));
        }
    }

    Array.prototype.forEach.call(defs, function (def, _) {
        const defelems = def.querySelectorAll('linearGradient, radialGradient, filter, marker, svg, symbol');
        let i = defelems.length;
        while (i--) {
            const defelem = defelems[i];
            const { id } = defelem;
            if (!defelemUses.includes(id)) {
                // Not found, so remove (but remember)
                svgCanvas["removedElements"][id] = defelem;
                defelem.remove();
                numRemoved++;
            }
        }
    });

    return numRemoved;
};
/**
* Converts gradients from userSpaceOnUse to objectBoundingBox.
* @function module:svgcanvas.SvgCanvas#convertGradients
* @param {Element} elem
* @returns {void}
*/
export const convertGradientsMethod = function (this: SvgCanvas, elem: SVGElement) {
    let elems: Element[] | NodeListOf<Element> = elem.querySelectorAll('linearGradient, radialGradient');
    if (!elems.length && isWebkit()) {
        // Bug in webkit prevents regular *Gradient selector search
        elems = Array.prototype.filter.call(elem.querySelectorAll('*'), function (curThis) {
            return (curThis.tagName.includes('Gradient'));
        });
    }

    Array.prototype.forEach.call(elems, function (grad) {
        if (grad.getAttribute('gradientUnits') === 'userSpaceOnUse') {
            const svgcontent = svgCanvas.getSVGContent();
            // TODO: Support more than one element with this ref by duplicating parent grad
            const fillStrokeElems = svgcontent.querySelectorAll<SVGGraphicsElement>('[fill="url(#' + grad.id + ')"],[stroke="url(#' + grad.id + ')"]');
            if (!fillStrokeElems.length) {
                return;
            }

            // get object's bounding box
            const bb = utilsGetBBox(fillStrokeElems[0]);

            // This will occur if the element is inside a <defs> or a <symbol>,
            // in which we shouldn't need to convert anyway.
            if (!bb) { return; }

            if (grad.tagName === 'linearGradient') {
                const gCoords = {
                    x1: grad.getAttribute('x1'),
                    y1: grad.getAttribute('y1'),
                    x2: grad.getAttribute('x2'),
                    y2: grad.getAttribute('y2'),
                };

                // If has transform, convert
                const tlist = grad.gradientTransform.baseVal;
                if (tlist && tlist.numberOfItems > 0) {
                    const m = transformListToTransform(tlist).matrix;
                    const pt1 = transformPoint(gCoords.x1, gCoords.y1, m);
                    const pt2 = transformPoint(gCoords.x2, gCoords.y2, m);

                    gCoords.x1 = pt1.x;
                    gCoords.y1 = pt1.y;
                    gCoords.x2 = pt2.x;
                    gCoords.y2 = pt2.y;
                    grad.removeAttribute('gradientTransform');
                }
                grad.setAttribute('x1', (gCoords.x1 - bb.x) / bb.width);
                grad.setAttribute('y1', (gCoords.y1 - bb.y) / bb.height);
                grad.setAttribute('x2', (gCoords.x2 - bb.x) / bb.width);
                grad.setAttribute('y2', (gCoords.y2 - bb.y) / bb.height);
                grad.removeAttribute('gradientUnits');
            }
        }
    });
};
